import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  fetchDistributionSetupData,
  fetchMyDistributionRecords,
  submitDistributionRecords,
  bulkUpdateDistributionRecords,
} from '../actions/distributionActions';
import {
  DISTRIBUTION_SUBMIT_RESET,
  DISTRIBUTION_BULK_UPDATE_RESET,
} from '../constants/distributionConstants';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

// setup data returns members as {name, identifier}; existing records return {memberName, amountGiven}
const buildEmptySection = (setupSection) => ({
  bank: setupSection.bank,
  product: setupSection.product,
  _id: null,
  billAmountAfterTDS: setupSection.billAmountAfterTDS ?? '',
  distributionPercent: setupSection.distributionPercent ?? 0,
  distributableAmount: setupSection.distributableAmount ?? '',
  totalTeamPayableSalary: setupSection.totalTeamPayableSalary ?? 0,
  supervisorAdvance: setupSection.supervisorAdvance ?? null,
  members: (setupSection.members || []).map((m) => ({
    memberName: typeof m === 'string' ? m : (m.name || m.memberName),
    amountGiven: '',
    advance: m.advance ?? null,
  })),
  isEditing: false,
});

// Sum all filled member amounts in a section
const getSectionTotal = (section) => {
  return section.members.reduce((acc, m) => {
    const v = parseFloat(m.amountGiven);
    return acc + (isNaN(v) ? 0 : v);
  }, 0);
};

const Distribution = () => {
  const dispatch = useDispatch();

  const [month, setMonth] = useState(CURRENT_MONTH);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [sections, setSections] = useState([]);

  const { userLogin: { userInfo } } = useSelector((s) => s);
  const { sections: setupSections = [], loading: loadingSetup } = useSelector((s) => s.distributionSetup);
  const { records: myRecords = [], loading: loadingRecords } = useSelector((s) => s.distributionMyRecords);
  const distributionSubmit = useSelector((s) => s.distributionSubmit);
  const distributionBulkUpdate = useSelector((s) => s.distributionBulkUpdate);

  const loadData = useCallback(() => {
    dispatch(fetchDistributionSetupData(month, year));
    dispatch(fetchMyDistributionRecords(month, year));
  }, [dispatch, month, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build sections whenever setup data or existing records change
  useEffect(() => {
    if (loadingSetup || loadingRecords) return;

    if (setupSections.length === 0 && myRecords.length === 0) {
      setSections([]);
      return;
    }

    const combos = setupSections.map((s) => ({ bank: s.bank, product: s.product }));
    for (const rec of myRecords) {
      const exists = combos.find((c) => c.bank === rec.bank && c.product === rec.product);
      if (!exists) combos.push({ bank: rec.bank, product: rec.product });
    }

    const built = combos.map(({ bank, product }) => {
      const setup = setupSections.find((s) => s.bank === bank && s.product === product);
      const existing = myRecords.find((r) => r.bank === bank && r.product === product);

      if (existing) {
        return {
          bank,
          product,
          _id: existing._id,
          billAmountAfterTDS: existing.billAmountAfterTDS ?? '',
          distributionPercent: existing.distributionPercent ?? 0,
          distributableAmount: existing.distributableAmount ?? '',
          totalTeamPayableSalary: setup?.totalTeamPayableSalary ?? 0,
          supervisorAdvance: setup?.supervisorAdvance ?? null,
          members: (existing.members || []).map((m) => {
            const setupMember = (setup?.members || []).find(
              (sm) => (sm.name || sm.memberName) === m.memberName,
            );
            return {
              memberName: m.memberName,
              amountGiven: m.amountGiven ?? '',
              advance: setupMember?.advance ?? null,
            };
          }),
          isEditing: false,
        };
      }

      return buildEmptySection(setup || { bank, product, members: [] });
    });

    setSections(built);
  }, [setupSections, myRecords, loadingSetup, loadingRecords]);

  // Submit success/fail
  useEffect(() => {
    if (distributionSubmit.success) {
      toast.success('Distribution records submitted successfully');
      dispatch({ type: DISTRIBUTION_SUBMIT_RESET });
      loadData();
    }
    if (distributionSubmit.error) {
      toast.error(distributionSubmit.error);
      dispatch({ type: DISTRIBUTION_SUBMIT_RESET });
    }
  }, [distributionSubmit.success, distributionSubmit.error]);

  // Bulk update success/fail
  useEffect(() => {
    if (distributionBulkUpdate.success) {
      toast.success('Distribution records updated successfully');
      dispatch({ type: DISTRIBUTION_BULK_UPDATE_RESET });
      loadData();
    }
    if (distributionBulkUpdate.error) {
      toast.error(distributionBulkUpdate.error);
      dispatch({ type: DISTRIBUTION_BULK_UPDATE_RESET });
    }
  }, [distributionBulkUpdate.success, distributionBulkUpdate.error]);

  const updateMemberAmount = (sectionIdx, memberIdx, value) => {
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== sectionIdx) return s;
        const members = s.members.map((m, j) =>
          j === memberIdx ? { ...m, amountGiven: value } : m,
        );
        return { ...s, members };
      }),
    );
  };

  const setSectionEditing = (idx, editing) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, isEditing: editing } : s)));
  };

  // Returns overrun error message or null for a section
  const getSectionError = (section) => {
    const distributable = parseFloat(section.distributableAmount);
    if (isNaN(distributable) || distributable === 0) return null;
    const total = getSectionTotal(section);
    if (total > distributable) {
      return `Total distributed (${total.toFixed(2)}) exceeds distributable amount (${distributable.toFixed(2)}) by ${(total - distributable).toFixed(2)}`;
    }
    return null;
  };

  const handleSubmit = (idx) => {
    const section = sections[idx];
    const err = getSectionError(section);
    if (err) return toast.error(`${section.bank} — ${section.product}: ${err}`);

    dispatch(submitDistributionRecords({
      month,
      year,
      sections: [{
        bank: section.bank,
        product: section.product,
        billAmountAfterTDS: section.billAmountAfterTDS,
        distributionPercent: section.distributionPercent,
        distributableAmount: section.distributableAmount,
        members: section.members.map((m) => ({
          memberName: m.memberName,
          amountGiven: m.amountGiven !== '' ? Number(m.amountGiven) : null,
        })),
      }],
    }));
  };

  const handleUpdate = (idx) => {
    const section = sections[idx];
    const err = getSectionError(section);
    if (err) return toast.error(`${section.bank} — ${section.product}: ${err}`);

    dispatch(bulkUpdateDistributionRecords([{
      _id: section._id,
      members: section.members.map((m) => ({
        memberName: m.memberName,
        amountGiven: m.amountGiven !== '' ? Number(m.amountGiven) : null,
      })),
    }]));
  };

  const isSaving = distributionSubmit.loading || distributionBulkUpdate.loading;
  const isLoading = loadingSetup || loadingRecords;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Distribution
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                {MONTH_NAMES.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sections.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No bank/product assignments found for {MONTH_NAMES[month - 1]} {year}.
            Please ensure Supervisor Master and Billing Master have entries for this month.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          {/* Sub-heading */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {userInfo?.name} &nbsp;·&nbsp; {MONTH_NAMES[month - 1]} {year}
            </Typography>
          </Box>

          {/* One section per bank+product */}
          {sections.map((section, sIdx) => {
            const isSubmitted = section._id != null;
            const isReadOnly = isSubmitted && !section.isEditing;
            const sectionError = getSectionError(section);
            const distributable = parseFloat(section.distributableAmount);
            const total = getSectionTotal(section);
            const remaining = isNaN(distributable) ? null : distributable - total;

            return (
              <Box key={`${section.bank}-${section.product}`} sx={{ mb: 4 }}>
                {/* Section header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ px: 1, whiteSpace: 'nowrap', color: 'text.secondary' }}
                  >
                    {section.bank} &nbsp;—&nbsp; {section.product}
                  </Typography>
                  {isSubmitted && (
                    <Chip
                      label={section.isEditing ? 'Editing' : 'Submitted'}
                      color={section.isEditing ? 'warning' : 'success'}
                      size="small"
                    />
                  )}
                  <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Info row: billing amounts (all read-only) */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth size="small"
                      label="Bill Amt After TDS"
                      value={section.billAmountAfterTDS}
                      InputProps={{ readOnly: true }}
                      helperText="From Billing"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth size="small"
                      label="Distribution %"
                      value={section.distributionPercent}
                      InputProps={{ readOnly: true }}
                      helperText="From Supervisor Master"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth size="small"
                      label="Distributable Amount"
                      value={section.distributableAmount}
                      InputProps={{ readOnly: true }}
                      helperText="Bill Amt × Distribution %"
                    />
                  </Grid>
                </Grid>

                {/* Supervisor Advance */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth size="small"
                      label={`Supervisor Advance (${userInfo?.name || 'Supervisor'})`}
                      value={section.supervisorAdvance ?? '—'}
                      InputProps={{ readOnly: true }}
                      helperText="From Supervisor Master"
                    />
                  </Grid>
                </Grid>

                {/* Team members */}
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Distribution
                </Typography>
                <Grid container spacing={2}>
                  {/* Team member fields */}
                  {section.members.map((member, mIdx) => (
                    <Grid item xs={12} sm={6} key={member.memberName}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          fullWidth size="small"
                          label={member.memberName}
                          type="number"
                          placeholder="Amount Given"
                          value={member.amountGiven}
                          onChange={(e) => updateMemberAmount(sIdx, mIdx, e.target.value)}
                          InputProps={{ readOnly: isReadOnly }}
                          disabled={isReadOnly}
                          inputProps={{ min: 0 }}
                        />
                        <TextField
                          size="small"
                          label="Advance"
                          value={member.advance ?? '—'}
                          InputProps={{ readOnly: true }}
                          helperText="From Supervisor Master"
                          sx={{ minWidth: 130 }}
                        />
                      </Box>
                    </Grid>
                  ))}

                  {section.members.length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No team members assigned for this bank &amp; product.
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Running total + error */}
                {!isReadOnly && !isNaN(distributable) && distributable > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    {sectionError ? (
                      <Alert severity="error" sx={{ py: 0.5 }}>
                        {sectionError}
                      </Alert>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Distributed: <strong>{total.toFixed(2)}</strong>
                        &nbsp;|&nbsp; Remaining: <strong style={{ color: remaining < 0 ? 'red' : 'green' }}>{remaining.toFixed(2)}</strong>
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Per-section action buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  {!isSubmitted && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleSubmit(sIdx)}
                      disabled={isSaving || sectionError != null}
                      startIcon={isSaving ? <CircularProgress size={16} /> : null}
                    >
                      {isSaving ? 'Submitting...' : 'Submit'}
                    </Button>
                  )}
                  {isSubmitted && !section.isEditing && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSectionEditing(sIdx, true)}
                    >
                      Edit
                    </Button>
                  )}
                  {isSubmitted && section.isEditing && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setSectionEditing(sIdx, false); loadData(); }}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUpdate(sIdx)}
                        disabled={isSaving || sectionError != null}
                        startIcon={isSaving ? <CircularProgress size={16} /> : null}
                      >
                        {isSaving ? 'Updating...' : 'Update'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default Distribution;
